/**
 * Blog Post Detail Page
 * Display individual blog post with full content
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Calendar, ExternalLink, Loader2, Quote, Clock, Link2 } from 'lucide-react';
import BlogCard from '@/components/BlogCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Components } from 'react-markdown';
import { ReadingProgress } from '@/components/ReadingProgress';
import {
  trackBlogPostView,
  trackBlogReadingTime,
  trackBlogScrollDepth,
  trackBlogSourceClick,
} from '@/services/analytics';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  summary: string;
  content: string;
  imageUrl: string;
  publishedAt: string;
  date: string; // Daily post date
  keywords: string[];
  sources: Array<{
    title: string;
    url: string;
    source: string;
  }>;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Analytics tracking refs
  const startTimeRef = useRef<number>(Date.now());
  const maxScrollDepthRef = useRef<number>(0);
  const scrollTrackedRef = useRef<Set<number>>(new Set()); // Track which scroll milestones we've logged

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        
        // Fetch all published blogs
        const response = await fetch('/api/blog/published');
        const data = await response.json();
        
        if (data.success) {
          const allPosts: BlogPost[] = data.data;
          const currentBlog = allPosts.find((b) => b.slug === slug);
          
          if (currentBlog) {
            setBlog(currentBlog);
            // Get related posts (3 most recent, excluding current)
            const related = allPosts
              .filter((b) => b.slug !== slug)
              .slice(0, 3);
            setRelatedPosts(related);
            
            // Track blog post view
            trackBlogPostView({
              postId: currentBlog.id,
              title: currentBlog.title,
              date: currentBlog.date,
              keywords: currentBlog.keywords,
              sourcesCount: currentBlog.sources.length,
            });
          } else {
            navigate('/blog');
          }
        } else {
          navigate('/blog');
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, navigate]);
  
  // Track reading time on unmount
  useEffect(() => {
    return () => {
      if (blog) {
        const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
        trackBlogReadingTime(blog.id, timeSpent);
      }
    };
  }, [blog]);
  
  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      if (!blog) return;
      
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);
      
      // Update max scroll depth
      if (scrollPercentage > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = scrollPercentage;
      }
      
      // Track milestones: 25%, 50%, 75%, 100%
      const milestones = [25, 50, 75, 100];
      milestones.forEach((milestone) => {
        if (
          scrollPercentage >= milestone &&
          !scrollTrackedRef.current.has(milestone)
        ) {
          scrollTrackedRef.current.add(milestone);
          trackBlogScrollDepth(blog.id, milestone);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [blog]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const shareUrl = blog ? `https://letsbereal.com.au/blog/${blog.slug}` : 'https://letsbereal.com.au/blog';

  // SEO - Call hook with default values when blog is null (hooks must be called unconditionally)
  usePageMeta({
    title: blog ? `${blog.title} | Let's Be Real` : "Property News | Let's Be Real",
    description: blog ? blog.summary : "Australian property news and insights",
    canonical: shareUrl,
    ogImage: blog?.imageUrl,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    keywords: blog?.keywords.join(', '),
    author: "Let's Be Real",
    publishedTime: blog?.publishedAt,
    schema: blog ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.summary,
      "image": blog.imageUrl,
      "datePublished": blog.publishedAt,
      "author": {
        "@type": "Organization",
        "name": "Let's Be Real"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Let's Be Real",
        "logo": {
          "@type": "ImageObject",
          "url": "https://letsbereal.com.au/lbr.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": shareUrl
      },
      "keywords": blog.keywords.join(', '),
      "articleSection": "Australian Property News",
      "inLanguage": "en-AU"
    } : undefined,
  });

  // Early returns AFTER all hooks are called
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  // Calculate reading time (approx 200 words per minute, ~5 chars per word)
  const readingTime = Math.max(1, Math.ceil(blog.content.length / 1000));

  return (
    <div className="min-h-screen bg-background">
      {/* Reading Progress Bar */}
      <ReadingProgress />

      {/* Back Button */}
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Button variant="ghost" onClick={() => navigate('/blog')} className="hover:bg-primary/5 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Property News
        </Button>
      </div>

      {/* Article Container with better spacing */}
      <article className="container mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="max-w-3xl mx-auto">
          
          {/* Header Section with enhanced typography */}
          <header className="mb-8 space-y-6">
            {/* Enhanced Meta Information */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <time dateTime={blog.publishedAt}>{formatDate(blog.publishedAt)}</time>
              </div>
              <span className="text-muted-foreground">•</span>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{readingTime} min read</span>
              </div>
              {blog.sources.length > 0 && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Link2 className="h-4 w-4" />
                    <span>{blog.sources.length} sources</span>
                  </div>
                </>
              )}
            </div>

            {/* Title with better spacing */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              {blog.title}
            </h1>

            {/* Summary with improved styling */}
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              {blog.summary}
            </p>

            {/* Keywords with enhanced design */}
            {blog.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {blog.keywords.map((keyword, idx) => (
                  <Badge 
                    key={idx} 
                    variant="secondary" 
                    className="px-3 py-1 text-sm font-medium"
                  >
                    {keyword}
                  </Badge>
                ))}
              </div>
            )}

            {/* Decorative divider */}
            <div className="w-20 h-1 bg-primary/20 rounded-full mt-6"></div>
          </header>

          {/* Hero Image with enhanced presentation */}
          <div className="mb-12">
            <div className="aspect-video rounded-xl overflow-hidden shadow-lg border-2 border-muted">
              <img
                src={blog.imageUrl}
                alt={blog.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>

          {/* Content with enhanced visual design */}
          <div className="space-y-8 mb-16">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Styled H2 headings with clean design
                h2: ({node, ...props}) => (
                  <div className="mt-12 sm:mt-16 mb-6 sm:mb-8 first:mt-0">
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight border-l-4 border-primary pl-3 sm:pl-4" {...props} />
                  </div>
                ),
                
                // Paragraphs with better spacing and styling
                p: ({node, ...props}) => {
                  const text = props.children?.toString() || '';
                  
                  // Check if paragraph starts with "POV:" - make it a callout
                  if (text.startsWith('POV:')) {
                    return (
                      <Card className="my-6 sm:my-8 border-l-4 border-l-primary bg-primary/5">
                        <CardContent className="py-4 sm:py-6">
                          <div className="flex gap-3 sm:gap-4">
                            <Quote className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0 mt-1" />
                            <p className="text-base sm:text-lg leading-relaxed m-0 font-medium" {...props} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }
                  
                  // Check for key insight paragraphs (containing certain keywords)
                  if (text.includes('takeaway') || text.includes('bottom line') || text.includes('key point')) {
                    return (
                      <div className="my-6 sm:my-8 p-4 sm:p-6 bg-primary/5 rounded-lg border-l-4 border-primary">
                        <p className="text-base sm:text-lg leading-relaxed m-0 font-medium" {...props} />
                      </div>
                    );
                  }
                  
                  return (
                    <p className="text-base sm:text-lg leading-relaxed my-4 sm:my-6 text-foreground/90" {...props} />
                  );
                },
                
                // Links with better styling
                a: ({node, ...props}) => (
                  <a 
                    className="text-primary font-semibold hover:underline decoration-2 underline-offset-4 transition-all inline-flex items-center gap-1" 
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {props.children}
                    <ExternalLink className="h-3 w-3 inline" />
                  </a>
                ),
                
                // Strong/bold text with emphasis
                strong: ({node, ...props}) => (
                  <strong className="font-bold text-foreground" {...props} />
                ),
                
                // Lists with better styling
                ul: ({node, ...props}) => (
                  <ul className="my-4 sm:my-6 space-y-2 sm:space-y-3 text-base sm:text-lg" {...props} />
                ),
                
                li: ({node, ...props}) => (
                  <li className="leading-relaxed pl-1 sm:pl-2" {...props} />
                ),
              } as Components}
            >
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* Sources Section with enhanced design */}
          {blog.sources.length > 0 && (
            <div className="mb-16">
              <Card className="border-2 shadow-sm">
                <CardContent className="pt-6">
                  <Accordion type="single" collapsible>
                    <AccordionItem value="sources" className="border-none">
                      <AccordionTrigger className="hover:no-underline py-3 sm:py-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                          </div>
                          <div className="text-left">
                            <div className="font-bold text-base sm:text-lg">News Sources</div>
                            <div className="text-xs sm:text-sm text-muted-foreground">{blog.sources.length} articles referenced</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-3 sm:pt-4">
                        <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-13">
                          {blog.sources.map((source, idx) => (
                            <a
                              key={idx}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block p-3 sm:p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all group"
                              onClick={() => {
                                trackBlogSourceClick({
                                  postId: blog.id,
                                  sourceName: source.source,
                                  sourceUrl: source.url,
                                  position: idx + 1,
                                });
                              }}
                            >
                              <div className="flex items-start gap-2 sm:gap-3">
                                <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm sm:text-base group-hover:text-primary transition-colors mb-1 break-words">
                                    {source.title}
                                  </div>
                                  <div className="text-xs sm:text-sm text-muted-foreground truncate">{source.source}</div>
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Related Posts with enhanced layout */}
          {relatedPosts.length > 0 && (
            <div className="mt-12 sm:mt-20 pt-8 sm:pt-12 border-t-2">
              <div className="text-center mb-8 sm:mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">More Property Insights</h2>
                <p className="text-sm sm:text-base text-muted-foreground">Stay updated with the latest Australian property news</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                {relatedPosts.map((post) => (
                  <BlogCard key={post.id} blog={post} />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}

